import { SeatMapBay } from '../../models/event-seat-map.interface';

/** Used for a ticket type that has no usable colour of its own. */
export const TIER_PALETTE = [
  '#8bb59b',
  '#e8d38a',
  '#9db7d5',
  '#d9a3a3',
  '#b9a6d3',
  '#a9cfc9',
  '#d8b98f',
];

export const INACTIVE_FILL = '#d6dbd6';
export const LABEL_COLOUR = '#14352b';

/** One ticket type as it appears on the map: the unit a customer filters and buys by. */
export interface SeatMapTier {
  ticketTypeUid: string;
  name: string;
  colour: string;
  price: number | null;
  currencyCode: string | null;
  availableSeats: number;
}

/**
 * How one bay is drawn. A bay is always painted a solid colour, never made translucent: whatever the
 * diagram has underneath it (an outline, a printed number, a pitch) must not show through a bay that is
 * meant to look switched off. Quiet bays are a lighter colour; only their label fades.
 */
export interface BayPaint {
  fill: string;
  /** the bay's outline: on a bay that can be bought it is a darker shade of its fill, so its shape holds against the page */
  stroke: string;
  dashed: boolean;
  /** a solid colour that passes 4.5:1 on `fill`; never an opacity, which would lower contrast */
  textColour: string;
}

/** Not on sale at all: lighter than sold out, so the two read differently. */
export const UNAVAILABLE_FILL = '#e9ece7';
/** Label colour on a bay that cannot be bought: passes 4.5:1 on every grey and lightened fill below. */
export const INACTIVE_TEXT = '#4f5e56';
/** Outline of a bay that cannot be bought; white separates it from its neighbours. */
export const QUIET_STROKE = '#ffffff';
/** Outline of a closed or part-closed bay: dashes only show against a colour that is not white. */
export const DASHED_STROKE = '#7b8a82';

const HEX_COLOUR = /^#[0-9a-fA-F]{3,8}$/;

/** Distinct ticket types in the order the map first shows them, each with a colour. */
export function tiersOf(bays: SeatMapBay[]): SeatMapTier[] {
  const tiers: SeatMapTier[] = [];
  for (const bay of bays) {
    if (!bay.ticketTypeUid) continue;
    const existing = tiers.find((t) => t.ticketTypeUid === bay.ticketTypeUid);
    if (existing) {
      existing.availableSeats += bay.availableSeats;
      continue;
    }
    const own = bay.ticketTypeColor?.trim() ?? '';
    tiers.push({
      ticketTypeUid: bay.ticketTypeUid,
      name: bay.ticketTypeName ?? 'Tickets',
      colour: HEX_COLOUR.test(own) ? own : TIER_PALETTE[tiers.length % TIER_PALETTE.length],
      price: bay.price,
      currencyCode: bay.currencyCode,
      availableSeats: bay.availableSeats,
    });
  }
  return tiers;
}

/** A bay can be chosen when it is on sale with seats left and the tier filter, if any, allows it. */
export function isPickable(bay: SeatMapBay, tierFilter: string | null): boolean {
  return (
    bay.status === 'AVAILABLE' &&
    !!bay.ticketTypeUid &&
    (tierFilter === null || tierFilter === bay.ticketTypeUid)
  );
}

export function formatPrice(price: number | null, currencyCode: string | null): string {
  if (price === null || price === undefined) return '';
  if (price === 0) return 'Free';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode || 'USD',
    }).format(price);
  } catch {
    return `${currencyCode ?? ''} ${price.toFixed(2)}`.trim();
  }
}

/** What the customer can do with a bay, in words: colour alone never carries this. */
export function statusText(bay: SeatMapBay): string {
  switch (bay.status) {
    case 'AVAILABLE':
      return `${bay.availableSeats} of ${bay.totalSeats} seats left`;
    case 'SOLD_OUT':
      return 'Sold out';
    case 'CLOSED':
      return 'Closed';
    default:
      return 'Not on sale';
  }
}

export function bayTitle(bay: SeatMapBay): string {
  return bay.name?.trim() || `Bay ${bay.code}`;
}

/** Spoken label for a bay: read by screen readers when the bay is focused. */
export function bayLabel(bay: SeatMapBay): string {
  const parts = [bayTitle(bay)];
  if (bay.ticketTypeName) parts.push(bay.ticketTypeName);
  const price = formatPrice(bay.price, bay.currencyCode);
  if (price) parts.push(price);
  parts.push(statusText(bay));
  if (bay.gate) parts.push(bay.gate);
  return parts.join(', ');
}

/** Mixes a #rgb or #rrggbb colour with white; `amount` 0 keeps it, 1 is white. Anything else is returned as is. */
export function lighten(hex: string, amount: number): string {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const full = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  const mixed = [0, 2, 4].map((i) => {
    const channel = parseInt(full.slice(i, i + 2), 16);
    return Math.round(channel + (255 - channel) * amount)
      .toString(16)
      .padStart(2, '0');
  });
  return `#${mixed.join('')}`;
}

/** Mixes a #rgb or #rrggbb colour with black; `amount` 0 keeps it, 1 is black. Anything else is returned as is. */
export function darken(hex: string, amount: number): string {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const full = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  const mixed = [0, 2, 4].map((i) =>
    Math.round(parseInt(full.slice(i, i + 2), 16) * (1 - amount))
      .toString(16)
      .padStart(2, '0')
  );
  return `#${mixed.join('')}`;
}

/** How a bay is drawn. Bays the API did not describe are drawn as not on sale. */
export function paintFor(
  bay: SeatMapBay | undefined,
  tiers: SeatMapTier[],
  tierFilter: string | null
): BayPaint {
  const quiet = (fill: string, dashed = false): BayPaint => ({
    fill,
    stroke: dashed ? DASHED_STROKE : QUIET_STROKE,
    dashed,
    textColour: INACTIVE_TEXT,
  });
  if (!bay) return quiet(UNAVAILABLE_FILL);
  switch (bay.status) {
    case 'AVAILABLE': {
      const colour =
        tiers.find((t) => t.ticketTypeUid === bay.ticketTypeUid)?.colour ?? INACTIVE_FILL;
      const shown = tierFilter === null || tierFilter === bay.ticketTypeUid;
      return shown
        ? {
            fill: colour,
            stroke: bay.hatched ? DASHED_STROKE : darken(colour, 0.35),
            dashed: bay.hatched,
            textColour: LABEL_COLOUR,
          }
        : quiet(lighten(colour, 0.75), bay.hatched);
    }
    case 'SOLD_OUT':
      return quiet(INACTIVE_FILL, bay.hatched);
    case 'CLOSED':
      return quiet(INACTIVE_FILL, true);
    default:
      return quiet(UNAVAILABLE_FILL);
  }
}

/**
 * Second line of defence for markup that is inlined into the page. The API already sanitises the SVG
 * with an allow-list before storing it; this removes active content again in the browser, so a bug or
 * a tampered response cannot run script here. Returns '' when the markup is not well-formed SVG.
 */
export function stripActiveContent(svg: string): string {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) {
    return '';
  }
  root
    .querySelectorAll('script, foreignObject, iframe, object, embed, style, image, a, animate, set')
    .forEach((el) => el.remove());
  const all = [root, ...Array.from(root.querySelectorAll('*'))];
  for (const el of all) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const isLink = name === 'href' || name === 'xlink:href';
      if (name.startsWith('on') || (isLink && !attr.value.startsWith('#'))) {
        el.removeAttribute(attr.name);
      }
    }
  }
  return new XMLSerializer().serializeToString(root);
}
